import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import WithRouter from "../../../../hoc/WithRouter";
import WithRoutify from "../../../../hoc/WithRoutify";
import EmployeeService from "../../../../services/EmployeeService";
import { getLookupByKey } from "../../../../utils/commonUtils";
import { SPECIALTY_REFERRAL } from "../../../../utils/constant";
import EmrVisitsService from "../../../../services/EmrVisitsService";
import ReferralProviderService from "../../../../services/ReferralProviderService";
import EmrStatusBar from "../ComplaintPage/EmrStatusBar";
import { Box, CircularProgress, Typography } from "@mui/material";

const ReferalsPage = (props) => {
  const [specialityTypes, setSpecialityTypes] = useState([]);
  const [referralOptions, setReferralOptions] = useState({}); // { [typeId]: [options] }
  const [allReferralProviders, setAllReferralProviders] = useState([]); // All referral providers from API
  const [checkedTypes, setCheckedTypes] = useState({}); // { [type.value]: boolean }
  const [selectedProviders, setSelectedProviders] = useState({}); // { [type.value]: provider_id }
  const [loading, setLoading] = useState(false);
  const visit_id = props?.visit_id; // Replace with real visit_id from props/context
  const { Toast, Loader, fetchProgressNotesByScreen, selectedSubTab, refreshEmrTrigger, incidentDetails } = props;
  const prevIncidentDetailsRef = useRef(null);
  const [totalReferrals,setTotalReferrals]=useState(34);
  const[outstanding,setOutStanding]=useState(12);
  const[approved,setApproved]=useState(22)
  
  // --- Fetch referral types on mount ---
  useEffect(() => {
    const fetchspecialityTypes = async () => {
      setLoading(true);
      try {
        const specialtyDropdown = await getLookupByKey(SPECIALTY_REFERRAL);
        console.log("Fetched referral types:", specialtyDropdown);

        const filteredList = Array.isArray(specialtyDropdown)
          ? specialtyDropdown.slice(1)
          : [];

        setSpecialityTypes(filteredList);
      } catch (err) {
        console.error("Error fetching referral types:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchspecialityTypes();
    fetchAllReferralProviders();
  }, []);

  // Fetch EMR referrals when both specialityTypes and visit_id are available
  useEffect(() => {
    if (visit_id && specialityTypes.length > 0) {
      fetchEmrReferrals();
    }
  }, [visit_id, specialityTypes]);

  // Fetch progress notes when REFERRALS tab is selected
  useEffect(() => {
    if (selectedSubTab === 'REFERRALS' && fetchProgressNotesByScreen && visit_id) {
      fetchProgressNotesByScreen();
    }
  }, [visit_id, selectedSubTab]); // Don't include fetchProgressNotesByScreen in dependencies to prevent infinite loops

  // Refresh referrals and progress notes when incident is updated (refreshEmrTrigger changes)
  useEffect(() => {
    if (refreshEmrTrigger !== undefined && refreshEmrTrigger > 0 && visit_id && selectedSubTab === 'REFERRALS') {
      // Refresh referrals data
      if (specialityTypes.length > 0) {
        fetchEmrReferrals();
      }
      // Refresh progress notes
      if (fetchProgressNotesByScreen) {
        fetchProgressNotesByScreen();
      }
    }
  }, [refreshEmrTrigger, visit_id, selectedSubTab]); // Refresh when trigger changes

  // Refresh referrals and progress notes when incident details change (incident saved/updated)
  useEffect(() => {
    if (!incidentDetails || !visit_id || selectedSubTab !== 'REFERRALS') {
      prevIncidentDetailsRef.current = incidentDetails;
      return;
    }

    // Check if incident details have changed (especially referral fields)
    const prevDetails = prevIncidentDetailsRef.current;
    if (prevDetails) {
      // List of referral fields to check
      const referralFields = [
        'acupuncture', 'cardiology', 'chiropractic', 'dermatology',
        'ent_ear_nose_throat', 'general_surgery', 'hand_surgery',
        'multi_specialties_organization', 'neurology', 'ophthalmology_optometry',
        'orthopedics', 'pain_management', 'physical_therapy', 'plastic_surgery',
        'primary_care_physician_pcp', 'psychology_psychiatry', 'pulmonology', 'urology'
      ];

      // Check if any referral field has changed
      const hasReferralChange = referralFields.some(field => {
        const referralField = `${field}_referral`;
        return (
          (incidentDetails[field] !== prevDetails[field]) ||
          (incidentDetails[referralField] !== prevDetails[referralField])
        );
      });

      if (hasReferralChange) {
        // Refresh referrals data
        if (specialityTypes.length > 0) {
          fetchEmrReferrals();
        }
        // Refresh progress notes
        if (fetchProgressNotesByScreen) {
          fetchProgressNotesByScreen();
        }
      }
    }

    // Update ref for next comparison
    prevIncidentDetailsRef.current = incidentDetails;
  }, [incidentDetails, visit_id, selectedSubTab, specialityTypes.length]); // Refresh when incident details change

  useEffect(() => {
    if (specialityTypes.length > 0 && allReferralProviders.length > 0) {
      const optionsBySpeciality = {};
      specialityTypes.forEach((type) => {
        optionsBySpeciality[type.value] = allReferralProviders;
      });
      setReferralOptions(optionsBySpeciality);
    }
  }, [specialityTypes, allReferralProviders]);

  const fetchAllReferralProviders = async () => {
    try {
      const response = await ReferralProviderService.getReferralProviders(null, {
        perPage: 1000,
        page: 1
      });      
      if (response && response.data && Array.isArray(response.data)) {
        const formattedProviders = response.data.map((provider) => ({
          value: provider.referral_provider_id,
          label: `${provider.first_name || ''} ${provider.last_name || ''}${provider.specialty ? ` (${provider.specialty})` : ''}`.trim(),
          name: `${provider.first_name || ''} ${provider.last_name || ''}`.trim(),
          specialty: provider.specialty || '',
          data: provider,
        }));
                setAllReferralProviders(formattedProviders);
      } else {
        console.warn('Unexpected response structure from referral-provider API:', response);
      }
    } catch (error) {
      console.error('Error fetching all referral providers:', error);
      setAllReferralProviders([]);
    }
  };
  // Helper function to convert field name to speciality name (reverse of getFieldName)
  const getSpecialityNameFromFieldName = (fieldName, specialityTypes) => {
    for (const type of specialityTypes) {
      const typeFieldName = type.value.toLowerCase()
        .replace(/\//g, '_')
        .replace(/\(/g, '')
        .replace(/\)/g, '')
        .replace(/&/g, '')
        .replace(/,/g, '')
        .replace(/-/g, '_')
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
      
      if (typeFieldName === fieldName) {
        return type.label || type.value;
      }
    }
    return null;
  };


  // Helper function to convert specialty type to field name (like backend getFieldName)
  const getFieldNameFromSpecialityType = (specialityType) => {
    const fieldName = (specialityType.value || specialityType.label || '').toLowerCase()
      .replace(/\//g, '_')
      .replace(/\(/g, '')
      .replace(/\)/g, '')
      .replace(/&/g, '')
      .replace(/,/g, '')
      .replace(/-/g, '_')
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
    return fieldName;
  };

  const fetchEmrReferrals = async () => {
    try {
      // Fetch EMR referrals from the API
      const emrResponse = await EmrVisitsService.getReferralSelection(visit_id).catch(() => ({ data: [] }));

      const emrReferralData = emrResponse?.data || [];
      console.log("referralRecord", emrReferralData);
      console.log("specialityTypes", specialityTypes);
      
      // Build maps for checkedTypes and selectedProviders
      // IMPORTANT: Use type.value as the key (not type.label) because that's what the UI uses
      const newChecked = {};
      const newSelected = {};
      const referralDataToProcess = [];

      // Process EMR referrals - API returns array with object containing field-based data
      if (Array.isArray(emrReferralData) && emrReferralData.length > 0 && specialityTypes.length > 0) {
        const referralRecord = emrReferralData[0]; // Get the first (and typically only) record
        
        const referralFields = [
          'acupuncture', 'cardiology', 'chiropractic', 'dermatology',
          'ent_ear_nose_throat', 'general_surgery', 'hand_surgery',
          'multi_specialties_organization', 'neurology', 'ophthalmology_optometry',
          'orthopedics', 'pain_management', 'physical_therapy', 'plastic_surgery',
          'primary_care_physician_pcp', 'psychology_psychiatry', 'pulmonology', 'urology'
        ];

        referralFields.forEach((fieldName) => {
          const referralFieldName = fieldName + '_referral';
          // Check if the field is set to 1 (checked) and has a referral provider ID
          if (referralRecord[fieldName] == 1 && referralRecord[referralFieldName]) {
            // Find the matching specialty type by comparing field names
            const specialityType = specialityTypes.find((type) => {
              const typeFieldName = type.value.toLowerCase()
                .replace(/\//g, '_')
                .replace(/\(/g, '')
                .replace(/\)/g, '')
                .replace(/&/g, '')
                .replace(/,/g, '')
                .replace(/-/g, '_')
                .replace(/\s+/g, '_')
                .replace(/[^a-z0-9_]/g, '')
                .replace(/_+/g, '_')
                .replace(/^_|_$/g, '');
              return typeFieldName === fieldName;
            });

            if (specialityType) {
              // Use type.value as the key (not type.label) to match the UI
              newChecked[specialityType.value] = true;
              newSelected[specialityType.value] = referralRecord[referralFieldName];
              
              referralDataToProcess.push({
                speciality_name: specialityType.label || specialityType.value,
                referral_provider_id: referralRecord[referralFieldName],
                lookup_id: specialityType.id
              });
              
              console.log(`Mapped ${fieldName} to ${specialityType.value} with provider ${referralRecord[referralFieldName]}`);
            } else {
              console.warn(`Could not find specialty type for field: ${fieldName}`);
            }
          }
        });
      }

      console.log("newChecked", newChecked);
      console.log("newSelected", newSelected);

      // Update state
      setCheckedTypes(newChecked);
      setSelectedProviders(newSelected);

      // Use already loaded referral providers for dropdown options
      // Show all providers for all checked speciality types (no filtering)
      if (allReferralProviders.length > 0) {
        const optionsBySpeciality = {};
        // Set options for all checked specialties
        Object.keys(newChecked).forEach((typeValue) => {
          if (newChecked[typeValue]) {
            optionsBySpeciality[typeValue] = allReferralProviders;
          }
        });
        
        setReferralOptions((prev) => ({
          ...prev,
          ...optionsBySpeciality,
        }));
      }
    } catch (err) {
      console.error("Error fetching saved referrals:", err);
    }
  };

  // --- Checkbox click handler ---
  const handleReferralCheckboxClick = async (type, event) => {
    const isChecked = event.target.checked;

    setCheckedTypes((prev) => ({
      ...prev,
      [type.value]: isChecked,
    }));

    if (!isChecked) {
      // Uncheck removes selection
      setSelectedProviders((prev) => {
        const updated = { ...prev };
        delete updated[type.value];
        return updated;
      });
    }

    // Use already loaded referral providers for dropdown options
    // Show all providers (no filtering)
    if (isChecked && !referralOptions[type.value] && allReferralProviders.length > 0) {
      console.log(
        "Using all referral options for:",
        type.value,
        allReferralProviders.length,
        "providers"
      );

      setReferralOptions((prev) => ({
        ...prev,
        [type.value]: allReferralProviders,
      }));
    } else if (isChecked && allReferralProviders.length === 0) {
      // If providers haven't loaded yet, fetch them
      fetchAllReferralProviders();
    }
  };

  // --- Referral dropdown change handler ---
  const handleReferralSelectChange = (type, event) => {
    const selectedId = event.target.value;
    setSelectedProviders((prev) => ({
      ...prev,
      [type.value]: selectedId,
    }));
  };

  // --- Save button handler ---
  const handleSaveReferrals = async () => {
    // Build payload in field-based format (like the API response)
    const payload = {};
    let hasAnyReferral = false;

    // List of all referral fields (same as in fetchEmrReferrals)
    const referralFields = [
      'acupuncture', 'cardiology', 'chiropractic', 'dermatology',
      'ent_ear_nose_throat', 'general_surgery', 'hand_surgery',
      'multi_specialties_organization', 'neurology', 'ophthalmology_optometry',
      'orthopedics', 'pain_management', 'physical_therapy', 'plastic_surgery',
      'primary_care_physician_pcp', 'psychology_psychiatry', 'pulmonology', 'urology'
    ];

    // Initialize all fields to 0 and referrals to null
    referralFields.forEach((fieldName) => {
      payload[fieldName] = 0;
      payload[fieldName + '_referral'] = null;
    });

    // Process each specialty type
    specialityTypes.forEach((type) => {
      const typeId = type.value;
      const fieldName = getFieldNameFromSpecialityType(type);
      
      if (checkedTypes[typeId] && selectedProviders[typeId]) {
        // Set to 1 if checked and has provider
        payload[fieldName] = 1;
        payload[fieldName + '_referral'] = selectedProviders[typeId];
        hasAnyReferral = true;
      } else {
        // Set to 0 if unchecked or no provider
        payload[fieldName] = 0;
        payload[fieldName + '_referral'] = null;
      }
    });

    if (!hasAnyReferral) {
      Toast.error("Please select at least one referral with a provider.");
      return;
    }

    console.log("Saving payload:", payload);

    try {
      const response = await EmrVisitsService.saveReferralSelection(
        visit_id,
        payload
      );
      console.log("Save response:", response);
      
      if (response.code === 200) {
        Toast.success("Referrals saved successfully!");
        // Refresh referrals to get synced data from incident
        await fetchEmrReferrals();
        
        // Reload progress notes for REFERRALS screen type
        if (fetchProgressNotesByScreen) {
          await fetchProgressNotesByScreen();
        }
      } else if (response.code === 404) {
        Toast.error("Incident not found");
      } else {
        Toast.error(response.message || "Failed to save referrals. Please try again.");
      }
    } catch (err) {
      console.error("Error saving referrals:", err);
      // Check if it's a 404 error from the catch
      if (err.response && err.response.status === 404) {
        Toast.error("Incident not found");
      } else {
        Toast.error("Failed to save referrals. Please try again.");
      }
    }
  };

  return (
    <>
      <div className="row">
         <div
        className="sh_cardHeader mb-2 rounded-2"
        style={{
          background: "linear-gradient(90deg, #FFF7DC 0%, #F7E9E5 100%)",
        }}
      >
        <div className="d-flex justify-content-center">
          <div className="form-group d-flex mb-0 justify-content-between align-items-center">
            <EmrStatusBar visitDetails={props.visitDetails} />
          </div>
        </div>
      </div>
        {/* Header cards */}
        <div className="sh_cardHeader px-0" style={{ paddingTop: 0 }}>
          
        <div className="sh_cardHeader px-0">
          <div className="row">
            <div className="col-md-4 col-6">
              <div className="d-flex align-items-center justify-content-between sh_statusIndicatorbox blue-orichid px-3 py-2">
                {/* Left section */}
                <div className="d-flex align-items-center gap-2">
                  <span className="sh_statusIndicator bg-light">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21.004 15.0014V14.001C21.0187 13.2006 20.7072 12.4287 20.1412 11.8626C19.5751 11.2966 18.8032 10.9851 18.0028 10.9998H16.002"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12.0001 15.0014V14.001C12.0148 13.2006 11.7033 12.4287 11.1372 11.8626C10.5712 11.2966 9.79924 10.9851 8.99885 10.9998H5.9976C5.1972 10.9851 4.42526 11.2966 3.85921 11.8626C3.29315 12.4287 2.98166 13.2006 2.99635 14.001V15.0014"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle
                        cx="7.49811"
                        cy="5.49762"
                        r="2.50104"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <ellipse
                        cx="17.0023"
                        cy="5.99742"
                        rx="2.00083"
                        ry="2.00083"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.0027 20.1084V17.5513H15.4097"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5.99756 17.5513C7.15645 19.6796 9.38601 21.0043 11.8094 21.0043C14.2328 21.0043 16.4624 19.6796 17.6213 17.5513"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>

                  {/* Center text */}
                  <div className="d-flex flex-column">
                    <h3 className="h3-strong mb-0 text-dark">{totalReferrals}</h3>
                    <p className="p1-light text-dark mb-0">Total Referrals</p>
                  </div>
                </div>

                {/* Right circular progress */}
                <Box position="relative" display="inline-flex">
                  {/* Grey border circle */}
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={60}
                    thickness={3}
                    sx={{
                      color: "#E0E0E0",
                      position: "absolute",
                    }}
                  />

                  {/* Progress circle */}
                  <CircularProgress
                    variant="determinate"
                    value={totalReferrals}
                    size={60}
                    thickness={3}
                    sx={{
                      color: "#5A6BF6",
                    }}
                  />

                  {/* Center text */}
                  <Box
                    top={0}
                    left={0}
                    bottom={0}
                    right={0}
                    position="absolute"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Typography
                      variant="caption"
                      component="div"
                      color="black"
                      fontWeight="600"
                    >
                      {`${totalReferrals}%`}
                    </Typography>
                  </Box>
                </Box>
              </div>
            </div>

            <div className="col-md-4 col-6">
              <div className="d-flex align-items-center justify-content-between sh_statusIndicatorbox pale-purple px-3 py-2">
                {/* Left section */}
                <div className="d-flex align-items-center gap-2">
                  <span className="sh_statusIndicator  bg-light">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 18H14"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M16.979 3H7.021C6.469 3 6.021 3.448 6.021 4V6.378C6.021 6.799 6.154 7.21 6.401 7.551L9.621 12L6.401 16.449C6.154 16.79 6.021 17.201 6.021 17.622V20C6.021 20.552 6.469 21 7.021 21H16.978C17.53 21 17.978 20.552 17.978 20V17.658C17.978 17.214 17.83 16.783 17.558 16.432L14.121 12L17.559 7.567C17.831 7.216 17.979 6.785 17.979 6.341V4C17.979 3.448 17.531 3 16.979 3Z"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  {/* Center Section */}
                  <div>
                    <h3 className="h3-strong mb-0 text-dark">{outstanding}</h3>
                    <p
                      className="p1-light text-dark mb-0"
                      style={{ lineHeight: "1.2" }}
                    >
                      Outstanding
                    </p>
                  </div>
                </div>
                {/* Right circular progress */}
                <Box position="relative" display="inline-flex">
                  {/* Grey trail */}
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={60}
                    thickness={3}
                    sx={{
                      color: "#E0E0E0",
                      position: "absolute",
                    }}
                  />
                  {/* Colored progress */}
                  <CircularProgress
                    variant="determinate"
                    value={outstanding}
                    size={60}
                    thickness={3}
                    sx={{
                      color: "#5A6BF6", // purple
                    }}
                  />
                  {/* Center percentage */}
                  <Box
                    top={0}
                    left={0}
                    bottom={0}
                    right={0}
                    position="absolute"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Typography
                      variant="caption"
                      component="div"
                      color="black"
                      fontWeight="600"
                    >
                      {outstanding}%
                    </Typography>
                  </Box>
                </Box>
              </div>
            </div>
            <div className="col-md-4 col-6">
              <div className="d-flex align-items-center justify-content-between sh_statusIndicatorbox olivegreen px-3 py-2">
                {/* Left section */}
                <div className="d-flex align-items-center gap-2">
                  <span className="sh_statusIndicator bg-light">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11 3H6C4.34315 3 3 4.34315 3 6V9"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M17 21H18C19.6569 21 21 19.6569 21 18V13"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <ellipse
                        cx="17"
                        cy="7"
                        rx="4"
                        ry="4"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.25 6.5L16.6883 8L15.75 7.1"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <ellipse
                        cx="8"
                        cy="13.5"
                        rx="2.5"
                        ry="2.5"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M13 21C13 19.6193 11.8807 18.5 10.5 18.5H5.5C4.11929 18.5 3 19.6193 3 21"
                        stroke="#1D2433"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>

                  {/* Center text */}
                  <div className="d-flex flex-column">
                    <h3 className="h3-strong mb-0 text-dark">{approved}</h3>
                    <p className="p1-light text-dark mb-0">Approved</p>
                  </div>
                </div>

                {/* Right circular progress */}
                <Box position="relative" display="inline-flex">
                  {/* Grey border circle */}
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={60}
                    thickness={3}
                    sx={{
                      color: "#E0E0E0",
                      position: "absolute",
                    }}
                  />

                  {/* Progress circle */}
                  <CircularProgress
                    variant="determinate"
                    value={approved}
                    size={60}
                    thickness={3}
                    sx={{
                      color: "#5A6BF6", // progress color
                    }}
                  />

                  {/* Center percentage text */}
                  <Box
                    top={0}
                    left={0}
                    bottom={0}
                    right={0}
                    position="absolute"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Typography
                      variant="caption"
                      component="div"
                      color="black"
                      fontWeight="600"
                    >
                      {`${approved}%`}
                    </Typography>
                  </Box>
                </Box>
              </div>
            </div>
          </div>
        </div>
      
        
        </div>

        {/* Table Section */}
        <div className="sh_cardBox mt-3 h-auto shadow-none border">
          {/* --- Save button at top --- */}
          <div className="d-flex justify-content-end ">
            <button
              className="btn btn-primary"
              onClick={handleSaveReferrals}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Referrals"}
            </button>
          </div>

          {loading ? (
            <p>Loading referral types...</p>
          ) : (
            <div
              style={{
                maxHeight: "350px", // adjust height as needed
                overflowY: "auto",
                border: "1px solid #ddd",
                borderRadius: "6px",
              }}
            >
              <table className="table dataTable sh_tablenew nowrap table-hover mb-0">
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    background: "#f8f9fa",
                    zIndex: 1,
                  }}
                >
                  <tr>
                    <th className="initialtd">
                      <div
                        className="form-check-inline"
                        style={{ verticalAlign: "baseline" }}
                      ></div>
                    </th>
                    <th>Type</th>
                    <th>Referral</th>
                  </tr>
                </thead>
                <tbody>
                  {specialityTypes.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center">
                        No referral types found
                      </td>
                    </tr>
                  ) : (
                    specialityTypes.map((type, index) => (
                      <tr key={type.value || index}>
                        <td className="initialtd">
                          <input
                            className="form-check"
                            type="checkbox"
                            id={`referral_${index}`}
                            checked={!!checkedTypes[type.value]}
                            onChange={(e) =>
                              handleReferralCheckboxClick(type, e)
                            }
                          />
                        </td>
                        <td className="w-50">{type.label}</td>
                        <td className="w-50">
                          <select
                            className="form-control form-control-solid"
                            disabled={!checkedTypes[type.value]}
                            value={selectedProviders[type.value] || ""}
                            onChange={(e) =>
                              handleReferralSelectChange(type, e)
                            }
                          >
                            <option value="">-Select-</option>
                            {referralOptions[type.value]?.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    stateList: state.session.profileStateList,
  };
};

export default connect(mapStateToProps)(WithRouter(WithRoutify(ReferalsPage)));
